import { getFontStack } from "../fonts";
import type { ScriptFallbackKey } from "../script-fallbacks";
import { sanitizeCssColor } from "./escape";
import {
  getPageMarginValue,
  getPageSizeValue,
} from "./page-geometry";
import { getPdfCodeThemeDefinition } from "./code-themes";
import {
  buildThemeCssVariables,
  getPdfThemeDefinition,
  resolveCodeTheme,
} from "./themes";
import {
  defaultPreviewSurfaceBackground,
  type PdfOptions,
} from "./types";

export function buildPageNumberMarginCss(
  options: PdfOptions,
  scriptFallbacks: readonly ScriptFallbackKey[] = [],
) {
  if (!options.pageNumbers) {
    return "";
  }

  const bodyFont = getFontStack(options.fontFamily, scriptFallbacks);

  // Paged.js paints page numbers via CSS margin boxes for preview and PDF.
  return `
@page {
  @bottom-center {
    color: var(--md-page-number);
    content: "Page " counter(page) " of " counter(pages);
    font-family: ${bodyFont};
    font-size: 8px;
    text-align: center;
    vertical-align: middle;
    width: 100%;
  }
}
`.trim();
}

export function buildPrintDocumentCss(
  options: PdfOptions,
  previewSurfaceBackground = defaultPreviewSurfaceBackground,
  {
    includePagedPageNumbers = false,
    includePreviewSheetChrome = false,
    scriptFallbacks = [],
  }: {
    includePagedPageNumbers?: boolean;
    includePreviewSheetChrome?: boolean;
    scriptFallbacks?: readonly ScriptFallbackKey[];
  } = {},
) {
  const pageSize = getPageSizeValue(options);
  const pageMargin = getPageMarginValue(options);
  const bodyFont = getFontStack(options.fontFamily, scriptFallbacks);
  const codeFont = getFontStack(options.codeFontFamily, scriptFallbacks);
  const theme = getPdfThemeDefinition(options.theme);
  const themeVars = buildThemeCssVariables(theme.tokens);
  const alertVars = theme.isDark
    ? `
  --md-alert-note: #58a6ff;
  --md-alert-tip: #3fb950;
  --md-alert-important: #a371f7;
  --md-alert-warning: #d29922;
  --md-alert-caution: #f85149;
`.trim()
    : `
  --md-alert-note: #0969da;
  --md-alert-tip: #1a7f37;
  --md-alert-important: #8250df;
  --md-alert-warning: #9a6700;
  --md-alert-caution: #cf222e;
`.trim();
  const resolvedCodeThemeId = resolveCodeTheme(options.theme, options.codeTheme);
  const codeTheme = getPdfCodeThemeDefinition(resolvedCodeThemeId);
  const borderlessCodeCss = codeTheme.borderless
    ? `
body[data-code-theme="${options.codeTheme}"] .markdown-preview pre {
  border: none;
}
`.trim()
    : "";
  const pageNumberCss = includePagedPageNumbers
    ? buildPageNumberMarginCss(options, scriptFallbacks)
    : "";
  const surfaceBackground = sanitizeCssColor(previewSurfaceBackground);
  const previewSheetChromeCss = includePreviewSheetChrome
    ? `
@media screen {
  html {
    background: ${surfaceBackground} !important;
    overflow: hidden;
    scrollbar-width: none;
  }

  html::-webkit-scrollbar {
    display: none;
  }

  body {
    /* Same token as host .preview-surface so sheet gaps match the pane. */
    background: ${surfaceBackground} !important;
    overflow: hidden;
    padding: 0;
  }

  /* Paged.js page chrome — visual sheets only; layout comes from @page.
     !important beats polyfill-injected defaults so stacked pages stay
     visually separate after preview(). */
  .pagedjs_pages {
    background: ${surfaceBackground} !important;
    display: flex !important;
    flex-direction: column !important;
    gap: 1.5rem !important;
    margin: 0 auto !important;
    padding: 0.25rem 0 0.5rem !important;
    width: fit-content !important;
  }

  .pagedjs_page {
    background: var(--md-page) !important;
    border: 1px solid var(--md-sheet-border) !important;
    border-radius: 0.5rem !important;
    /* Keep shadow tight so it does not tint the gap darker than the pane. */
    box-shadow: var(--md-sheet-shadow) !important;
    color: var(--md-fg) !important;
    flex: 0 0 auto !important;
    margin: 0 !important;
    overflow: hidden !important;
    position: relative !important;
  }

  .pagedjs_sheet,
  .pagedjs_page_content,
  .pagedjs_area {
    background: var(--md-page) !important;
    color: var(--md-fg) !important;
  }

  .pagedjs_sheet {
    border-radius: inherit;
    overflow: hidden;
  }

  /* Center margin-box page numbers to match previous Chromium footer look. */
  .pagedjs_margin-bottom-center,
  .pagedjs_margin-top-center {
    align-items: center !important;
    display: flex !important;
    justify-content: center !important;
    text-align: center !important;
    width: 100% !important;
  }

  .pagedjs_margin-bottom-center > *,
  .pagedjs_margin-top-center > * {
    text-align: center !important;
    width: 100%;
  }
}
`.trim()
    : "";

  return `
@page {
  size: ${pageSize};
  margin: ${pageMargin};
  background: var(--md-page);
}

${pageNumberCss}

:root {
  --markdown2pdf-body-font: ${bodyFont};
  --markdown2pdf-code-font: ${codeFont};
  --markdown2pdf-code-font-size: ${options.codeFontSize}pt;
  ${themeVars};
  ${alertVars};
}

*,
*::before,
*::after {
  box-sizing: border-box;
}

html {
  background: var(--md-page);
  color: var(--md-fg);
  font-size: ${options.fontSize}pt;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

body {
  margin: 0;
  background: var(--md-page);
  color: var(--md-fg);
  font-family: var(--markdown2pdf-body-font);
  font-size: 1rem;
  line-height: 1.6;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.pdf-document {
  width: 100%;
}

.markdown-preview {
  color: var(--md-fg);
  overflow-wrap: break-word;
}

.markdown-preview > :first-child {
  margin-top: 0;
}

.markdown-preview > :last-child {
  margin-bottom: 0;
}

.markdown-preview p,
.markdown-preview ul,
.markdown-preview ol,
.markdown-preview blockquote,
.markdown-preview pre,
.markdown-preview table {
  margin-bottom: 0.9rem;
  margin-top: 0;
}

/* Hard breaks as blocks: Paged.js duplicates identical tokens when
   fragmenting <br>-split paragraphs (pagedjs#208). Visual parity with <br>.
   Empty lines carry a non-breaking space from the HTML pipeline so they
   keep a line box instead of collapsing. */
.markdown-preview .md-line {
  display: block;
}

.markdown-preview h1,
.markdown-preview h2,
.markdown-preview h3,
.markdown-preview h4,
.markdown-preview h5,
.markdown-preview h6 {
  color: var(--md-heading);
  font-weight: 700;
  line-height: 1.25;
  margin: 1.35rem 0 0.65rem;
  page-break-after: avoid;
}

.markdown-preview h1 {
  border-bottom: 1px solid var(--md-border);
  font-size: 2rem;
  padding-bottom: 0.35rem;
}

.markdown-preview h2 {
  border-bottom: 1px solid var(--md-border-subtle);
  font-size: 1.45rem;
  padding-bottom: 0.3rem;
}

/* GFM footnote label uses .sr-only; print CSS has no Tailwind utilities. */
.markdown-preview .sr-only,
.markdown-preview #footnote-label {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
  page-break-after: auto;
}

.markdown-preview h3 {
  font-size: 1.2rem;
}

.markdown-preview h4 {
  font-size: 1.05rem;
}

.markdown-preview h5,
.markdown-preview h6 {
  font-size: 0.95rem;
}

/* Preview keep-with-next wrappers (Paged.js KeepWithNext handler). */
.markdown-preview .md-keep-with-next {
  break-inside: avoid;
  page-break-inside: avoid;
}

.markdown-preview a {
  /* Match Chromium print: links inherit body color (no blue screen links). */
  color: inherit;
  text-decoration: underline;
  text-decoration-thickness: 0.05em;
  text-underline-offset: 2px;
}

.markdown-preview u {
  text-decoration: underline;
  text-decoration-thickness: 0.05em;
  text-underline-offset: 2px;
}

.markdown-preview mark {
  background: var(--md-mark-bg);
  color: inherit;
  padding: 0.05em 0.15em;
}

.markdown-preview dl {
  margin: 0 0 1rem;
}

.markdown-preview dt {
  font-weight: 600;
}

.markdown-preview dd {
  margin-block: 0.25rem 0.75rem;
  margin-inline-start: 1.5rem;
}

/*
 * Shared gutter for bullets, ordinals, and checkboxes so marker columns and
 * item text line up. Native list-style discs/decimals position inconsistently
 * (and outside decimals clip into @page margins). data-item-num markers also
 * survive Paged.js <ol> splits (pagedjs#280).
 */
.markdown-preview ul,
.markdown-preview ol {
  list-style: none;
  padding-inline-start: 2em;
}

.markdown-preview ul > li,
.markdown-preview ol > li {
  position: relative;
}

.markdown-preview ul > li::before,
.markdown-preview ol > li::before {
  inset-inline-start: -2em;
  position: absolute;
  text-align: end;
  white-space: nowrap;
  width: 1.75em;
}

.markdown-preview ul > li::before {
  content: "•";
}

.markdown-preview ol > li::before {
  content: attr(data-item-num) ".";
}

.markdown-preview li + li {
  margin-top: 0.25rem;
}

.markdown-preview li {
  /* Match Chromium print fragmentation: do not slice through a list item. */
  break-inside: avoid;
  page-break-inside: avoid;
}

.markdown-preview .contains-task-list > li::before {
  content: none;
}

.markdown-preview .task-list-item {
  list-style: none;
  position: relative;
}

.markdown-preview .task-list-item input[type="checkbox"] {
  /* Same 1.75em gutter column as • / 1. — box sits on the text-facing edge. */
  height: 1em;
  inset-inline-start: calc(-2em + 1.75em - 1em);
  margin: 0.3em 0 0;
  position: absolute;
  width: 1em;
}

.markdown-preview blockquote {
  border-inline-start: 0.25rem solid var(--md-border);
  color: var(--md-muted);
  padding-inline-start: 1rem;
}

.markdown-preview .markdown-alert {
  border-inline-start: 0.25em solid var(--md-border);
  color: inherit;
  margin-bottom: 1rem;
  padding: 0.5rem 1em;
}

.markdown-preview .markdown-alert > :last-child {
  margin-bottom: 0;
}

.markdown-preview .markdown-alert-title {
  align-items: center;
  display: flex;
  font-size: 0.875em;
  font-weight: 600;
  line-height: 1;
  margin-bottom: 0.5rem;
}

.markdown-preview .markdown-alert-title svg.octicon {
  fill: currentColor;
  flex-shrink: 0;
  height: 1em;
  margin-inline-end: 0.5rem;
  width: 1em;
}

.markdown-preview .markdown-alert-note {
  border-inline-start-color: var(--md-alert-note);
}

.markdown-preview .markdown-alert-note .markdown-alert-title {
  color: var(--md-alert-note);
}

.markdown-preview .markdown-alert-tip {
  border-inline-start-color: var(--md-alert-tip);
}

.markdown-preview .markdown-alert-tip .markdown-alert-title {
  color: var(--md-alert-tip);
}

.markdown-preview .markdown-alert-important {
  border-inline-start-color: var(--md-alert-important);
}

.markdown-preview .markdown-alert-important .markdown-alert-title {
  color: var(--md-alert-important);
}

.markdown-preview .markdown-alert-warning {
  border-inline-start-color: var(--md-alert-warning);
}

.markdown-preview .markdown-alert-warning .markdown-alert-title {
  color: var(--md-alert-warning);
}

.markdown-preview .markdown-alert-caution {
  border-inline-start-color: var(--md-alert-caution);
}

.markdown-preview .markdown-alert-caution .markdown-alert-title {
  color: var(--md-alert-caution);
}

.markdown-preview details {
  margin-bottom: 1rem;
}

.markdown-preview summary {
  cursor: pointer;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.markdown-preview details[data-split-from] > summary {
  color: var(--md-muted);
  font-weight: 600;
}

.markdown-preview code {
  background: var(--md-code-bg);
  border-radius: 4px;
  direction: ltr;
  font-family: var(--markdown2pdf-code-font);
  font-size: var(--markdown2pdf-code-font-size);
  padding: 0.15rem 0.3rem;
  unicode-bidi: isolate;
}

.markdown-preview pre {
  background: var(--md-code-bg);
  border: 1px solid var(--md-border);
  border-radius: 6px;
  /* Allow tall fences to fragment across pages. break-inside:avoid clips
     overflow under Paged.js (overflow:hidden page areas) instead of
     continuing the block — preview and PDF both lose the rest of the code. */
  direction: ltr;
  overflow-wrap: anywhere;
  padding: 1rem;
  unicode-bidi: isolate;
  white-space: pre-wrap;
}

${borderlessCodeCss}

/* Soften seams where a fenced block continues on the next page. Zero the
   duplicated padding/border on the cut edges — otherwise Chromium often
   spills a near-empty continuation onto an extra blank page (Firefox packs
   tighter and usually does not). */
.markdown-preview pre[data-split-to] {
  border-bottom: none;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  margin-bottom: 0;
  padding-bottom: 0;
}

.markdown-preview pre[data-split-from] {
  border-top: none;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  padding-top: 0;
}

.markdown-preview pre code {
  background: transparent;
  border-radius: 0;
  display: block;
  font-size: var(--markdown2pdf-code-font-size);
  padding: 0;
  white-space: inherit;
}

/* Block lines survive Paged.js <pre> splits (whitespace text nodes are
   dropped on continuation pages — pagedjs#45/#75). Keep each source line
   atomic so a soft-wrapped line cannot leave the next sibling stranded in
   the overflow column (invisible under overflow:hidden page boxes). */
.markdown-preview .md-code-line {
  break-inside: avoid;
  display: block;
  page-break-inside: avoid;
}

.markdown-preview pre.shiki {
  color: var(--md-fg);
}

.markdown-preview pre.shiki code {
  color: inherit;
}

.markdown-preview .shiki-token {
  background: transparent;
}

.markdown-preview .katex {
  font-size: 1.05em;
}

.markdown-preview math[display="block"] {
  display: block;
  margin: 1rem 0;
  overflow-x: auto;
  overflow-y: hidden;
  text-align: start;
}

.markdown-preview .katex-error {
  background: var(--md-katex-error-bg);
  border-radius: 4px;
  color: var(--md-katex-error-text);
  font-family: var(--markdown2pdf-code-font);
  padding: 0.1rem 0.25rem;
}

.markdown-preview .mermaid-diagram {
  break-inside: avoid;
  margin: 0.55rem 0;
  overflow-x: auto;
  text-align: center;
}

.markdown-preview .mermaid-diagram svg {
  display: inline-block;
  height: auto;
  max-width: 100%;
}

.markdown-preview .mermaid-diagram-fallback {
  background: var(--md-mermaid-fallback-bg);
  border: 1px solid var(--md-mermaid-fallback-border);
  border-radius: 6px;
  color: var(--md-mermaid-fallback-text);
  padding: 0.85rem;
  text-align: start;
}

.markdown-preview .mermaid-diagram-fallback p {
  font-weight: 700;
  margin: 0 0 0.65rem;
}

.markdown-preview .mermaid-source {
  margin: 0;
}

.markdown-preview table {
  border-collapse: collapse;
  width: 100%;
}

.markdown-preview th,
.markdown-preview td {
  border: 1px solid var(--md-border);
  padding: 0.45rem 0.65rem;
  text-align: start;
  vertical-align: top;
}

.markdown-preview th {
  background: var(--md-table-header-bg);
  font-weight: 700;
}

/* Firefox table flatten (see paged-preview-bootstrap): real <table> layout
   does not fragment in Paged.js columns. Rows become grid tracks so they
   paginate like blocks; keep cells from splitting mid-row. */
.markdown-preview table.md-table-flat,
.markdown-preview table.md-table-flat > thead,
.markdown-preview table.md-table-flat > tbody,
.markdown-preview table.md-table-flat > tfoot {
  display: block;
  width: 100%;
}

.markdown-preview table.md-table-flat > * > tr,
.markdown-preview table.md-table-flat > tr {
  box-sizing: border-box;
  break-inside: avoid;
  display: grid;
  grid-template-columns: repeat(var(--md-table-cols, 2), minmax(0, 1fr));
  page-break-inside: avoid;
  width: 100%;
}

.markdown-preview table.md-table-flat th,
.markdown-preview table.md-table-flat td {
  display: block;
  min-width: 0;
}

${previewSheetChromeCss}

@media print {
  body {
    padding: 0;
  }
}
`.trim();
}
